import {
 Box,
 Button,
 MobileStepper,
 Stack,
 Typography,
 useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardWidgetNav from './DashboardWidgetNav';
import customColors from '../../../utils/customColors';
import type { WidgetItemType } from '../Dashboard.types';

type SemanticColor = 'success' | 'error' | 'warning' | 'info';

const DashboardWidgetProgressHalf = ({
 widgetContent,

 totalSum: _totalSum,
}: {
 widgetContent: WidgetItemType[];

 totalSum: number;
}) => {
 const theme = useTheme();
 const { t } = useTranslation();
 const getAggregateValue = (item: WidgetItemType): number =>
  item.Aggregates['CargoNeto.sum'] ??
  item.Aggregates['neto.sum'] ??
  item.Aggregates['Neto.sum'] ??
  0;

 const maxAbs = useMemo(() => {
  let max = 0;
  for (const item of widgetContent) {
   const raw =
    item.Aggregates['CargoNeto.sum'] ??
    item.Aggregates['neto.sum'] ??
    item.Aggregates['Neto.sum'] ??
    0;
   const v = Math.abs(raw);
   if (v > max) {
    max = v;
   }
  }
  return max;
 }, [widgetContent]);

 const colorMap = useMemo(() => {
  const colors: SemanticColor[] = ['success', 'error', 'warning', 'info'];
  const map = new Map<string, SemanticColor>();
  widgetContent.forEach((item, index) => {
   map.set(item.Key, colors[index % colors.length]);
  });
  return map;
 }, [widgetContent]);

 const getSemanticFillColor = (themeArg: Theme, key: SemanticColor): string =>
  themeArg.palette[key].main;

 const getSemanticLightBgColor = (
  themeArg: Theme,
  key: SemanticColor,
 ): string => {
  const kemek = themeArg.palette.kemek;
  switch (key) {
   case 'success':
    return kemek.lightGreen;
   case 'error':
    return kemek.lightRed;
   case 'warning':
    return kemek.lightYellow;
   case 'info':
    return '#c2ceff';
  }
 };

 const [currentPage, setCurrentPage] = useState(1);
 const itemsPerPage = 10;
 const totalPages = Math.ceil(widgetContent.length / itemsPerPage);
 const startIndex = (currentPage - 1) * itemsPerPage;
 const endIndex = startIndex + itemsPerPage;
 const currentItems = widgetContent.slice(startIndex, endIndex);

 return (
  <Box
   sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflow: 'auto',
   }}
  >
   <Box
    sx={{
     display: 'flex',
     flexDirection: 'column',
     gap: 2,
     pr: 2,
     overflow: 'auto',
    }}
   >
    {currentItems.map((item) => {
     const value = getAggregateValue(item);
     const semantic = colorMap.get(item.Key) ?? 'info';
     const fillColor = getSemanticFillColor(theme, semantic);
     const trackBgColor = getSemanticLightBgColor(theme, semantic);
     const fillHalfRatio = maxAbs > 0 ? (Math.abs(value) / maxAbs) * 50 : 0;
     const signedPercentLabel =
      maxAbs > 0 ? ((value / maxAbs) * 100).toFixed(0) : '0';

     return (
      <Box key={item.Key}>
       <Box sx={{ display: 'flex', gap: '12px', mb: '4px' }}>
        <Typography sx={{ fontSize: 12, marginRight: 'auto' }}>
         {item.Key}
        </Typography>

        <Typography sx={{ fontSize: 12 }}>
         {value.toLocaleString()} t
        </Typography>

        <Typography sx={{ fontSize: 12, color: customColors.gray }}>
         {signedPercentLabel}%
        </Typography>
       </Box>

       <Box
        sx={{
         position: 'relative',
         height: 4,
         borderRadius: 4,
         bgcolor: trackBgColor,
        }}
       >
        {value !== 0 && maxAbs > 0 && (
         <Box
          sx={{
           position: 'absolute',
           top: 0,
           bottom: 0,
           ...(value > 0
            ? {
               left: '50%',
               width: `${fillHalfRatio}%`,
               borderRadius: 4,
               borderTopLeftRadius: 0,
               borderBottomLeftRadius: 0,
              }
            : {
               left: `${50 - fillHalfRatio}%`,
               width: `${fillHalfRatio}%`,
               borderRadius: 4,
               borderTopRightRadius: 0,
               borderBottomRightRadius: 0,
              }),
           bgcolor: fillColor,
          }}
         />
        )}
       </Box>
      </Box>
     );
    })}
   </Box>

   <DashboardWidgetNav
    totalPages={totalPages}
    currentPage={currentPage}
    setCurrentPage={setCurrentPage}
   />
  </Box>
 );
};

export default DashboardWidgetProgressHalf;
